import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { IPagedCollection } from '../../extViewmodels';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { ISupportSavedReply } from '../../models/Support/ISupportSavedReply';
import { emptyOnForbidden, supportQueryRetry } from '../../utils/Support/supportHelpers';
import { SUPPORT_PATHS, SUPPORT_QUERY_KEYS } from './supportQueryHelpers';

interface UseSavedReplyMutationOptions {
	onSuccess?: () => void;
	onError?: (error: unknown) => void;
}

export function useSavedRepliesQuery(mailboxId: string) {
	const userSession = useUserSession();
	return useQuery({
		queryKey: SUPPORT_QUERY_KEYS.mailboxes.savedReplies(mailboxId),
		staleTime: 30_000,
		retry: supportQueryRetry,
		queryFn: () =>
			emptyOnForbidden<IPagedCollection<ISupportSavedReply>>(
				() =>
					userSession.webServiceHelper.callAsync<IPagedCollection<ISupportSavedReply>>(
						SUPPORT_PATHS.mailboxes.savedReplies(mailboxId),
						'GET'
					),
				{ values: [] }
			),
		enabled: !!mailboxId,
		meta: { toastErrorMessage: 'Failed to load saved replies' },
	});
}

export function useCreateSavedReplyMutation(
	mailboxId: string,
	{ onSuccess, onError }: UseSavedReplyMutationOptions = {}
) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (reply: { name: string; body: string }) =>
			userSession.webServiceHelper.callAsync(SUPPORT_PATHS.mailboxes.savedReplies(mailboxId), 'POST', reply),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: SUPPORT_QUERY_KEYS.mailboxes.savedReplies(mailboxId) });
			onSuccess?.();
		},
		onError,
	});
}

export function useUpdateSavedReplyMutation(
	mailboxId: string,
	{ onSuccess, onError }: UseSavedReplyMutationOptions = {}
) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ id, name, body }: { id: string; name: string; body: string }) =>
			userSession.webServiceHelper.callAsync(SUPPORT_PATHS.mailboxes.savedReply(mailboxId, id), 'PUT', {
				name,
				body,
			}),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: SUPPORT_QUERY_KEYS.mailboxes.savedReplies(mailboxId) });
			onSuccess?.();
		},
		onError,
	});
}

export function useDeleteSavedReplyMutation(
	mailboxId: string,
	{ onSuccess, onError }: UseSavedReplyMutationOptions = {}
) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (id: string) =>
			userSession.webServiceHelper.callAsync(SUPPORT_PATHS.mailboxes.savedReply(mailboxId, id), 'DELETE'),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: SUPPORT_QUERY_KEYS.mailboxes.savedReplies(mailboxId) });
			onSuccess?.();
		},
		onError,
	});
}
